<template>
  <button
    :type="props.type ?? 'button'"
    @click="emit('click', $event)"
    :class="[
      { disabled: props.disabled },
      props.theme ?? 'primary',
      props.size ?? 'md',
      props.filled ? 'filled' : 'outlined',
    ]"
  >
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
const props = defineProps<{
  type?: "submit" | "button";

  // If true, the background is filled with the theme color.
  filled?: boolean;

  size?: "xs" | "sm" | "md" | "lg" | "xl";

  // The theme colors are named like in bootstrap.
  // Primary and secondary are colorless. Success is green, danger is red, etc.
  // More info: https://getbootstrap.com/docs/4.0/utilities/colors/
  theme?: "primary" | "secondary" | "link" | "danger" | "success";

  disabled?: boolean;
}>();
const emit = defineEmits(["click"]);
</script>

<style scoped lang="scss">
button {
  @apply font-bold w-fit transition-colors;

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &.xs {
    @apply text-xs py-1 px-2;
  }

  &.sm {
    @apply text-sm py-1.5 px-3;
  }

  &.md {
    @apply py-2 px-4;
  }

  &.lg {
    @apply text-lg py-2 px-5;
  }

  &.xl {
    @apply text-xl py-2.5 px-6;
  }

  &.primary {
    @apply text-grey-100;
    @apply border-grey-100 border-solid border-3 rounded-[2.5rem];
    @apply hover:text-white;
    @apply dark:text-white dark:border-white;

    &.filled {
      @apply bg-grey-100 text-white;
      @apply dark:hover:text-grey-100 dark:hover:bg-white;
      @apply dark:bg-white dark:text-grey-100;
    }

    &:not(.filled) {
      @apply hover:border-xdc-green-100 hover:text-xdc-green-100;
    }
  }

  &.link {
    @apply text-grey-100;
    @apply underline hover:no-underline hover:text-xdc-green-100;
    @apply dark:text-white dark:hover:text-xdc-green-100;
  }

  &.success {
    @apply text-xdc-green-100;
    @apply border-xdc-green-100 border-solid border-3 rounded-[2.5rem];

    &.filled {
      @apply bg-xdc-green-100 text-grey-100 hover:bg-xdc-green-100;
    }
  }

  &.danger {
    @apply text-danger-100;
    @apply border-danger-100 border-solid border-3 rounded-[2.5rem];
    @apply hover:text-white;

    &.filled {
      @apply bg-danger-100 text-white hover:bg-danger-100;
    }

    &:not(.filled) {
      @apply hover:text-grey-100 hover:border-grey-100;
      @apply dark:hover:text-white dark:hover:border-white;
    }
  }

  &.secondary {
    @apply text-grey-80;
    @apply border-solid border-3 border-grey-30 rounded-full;
    @apply hover:text-grey-100 hover:border-grey-30;
    @apply dark:text-grey-30;
    @apply dark:border-solid dark:border-3 dark:border-grey-80;
    @apply dark:hover:text-white;

    &.filled {
      @apply text-grey-100 hover:bg-grey-30;
      @apply bg-grey-10 border-solid border-3 border-grey-10 rounded-full;
      @apply hover:text-grey-100 hover:bg-grey-30 hover:border-grey-30;
      @apply dark:text-grey-10;
      @apply dark:bg-grey-80 dark:border-solid dark:border-3 dark:border-grey-80 dark:rounded-full;
      @apply dark:hover:text-white;
    }

    &:not(.filled) {
      @apply hover:text-grey-100 hover:border-grey-100;
      @apply dark:hover:text-white dark:hover:border-grey-30;
    }
  }
}
</style>
